import { default as FulfillmentSettingsInputs } from '@/components/forms/Locations/FulfillmentSettingsInputs'
import { default as WeeklyHoursInputs } from '@/components/forms/Locations/WeeklyHoursInputs'
import SaveBar from '@/components/forms/SaveBar'
import { DeliveryAndAvailabilityHeader } from '@/components/locations/DeliveryAndAvailabilityHeader'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { useIsReadOnly } from '@/lib/casl'
import { getDefaultHours } from '@/lib/constants/businessHours'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { useAppForm } from '@/lib/form'
import {
  locationFilters,
  locationQueries,
  useLocation,
  useUpdateLocationHoursSettingsMutation,
} from '@/lib/services/locations/locations.query'
import {
  locationIdSchema,
  locationPickupSettingsSchema,
} from '@/lib/services/locations/locations.schema'
import { getErrorMessage } from '@/lib/utils'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { Clock } from 'lucide-react'
import { toast } from 'sonner'

const filters = locationFilters().get()
const DEFAULT_PICKUP_CUTOFF_HOURS = 2
const canAccessLocationHours = (ability) =>
  ability.can('list', 'locations') &&
  (ability.can('view', 'location-hours') || ability.can('edit', 'location-hours'))

export const Route = createFileRoute(
  '/_authenticated/$orgId/settings/locations/$locationId/hours/'
)({
  component: LocationHoursAndSettings,
  head: adminRouteTitleHead,

  params: {
    parse: (params) => locationIdSchema.parse(params),
  },
  loader: async ({ context, params }) => {
    if (!canAccessLocationHours(context.ability)) throw notFound()

    void context.queryClient.ensureQueryData(
      locationQueries.show({
        organizationId: params.orgId,
        locationId: params.locationId,
        filters,
      })
    )
    return { isReadOnly: !context.ability.can('edit', 'location-hours') }
  },
})

export function LocationHoursAndSettings() {
  const { locationId } = Route.useParams()
  const { data: location } = useLocation(locationId, { filters })
  const updateLocationSettingsMutation = useUpdateLocationHoursSettingsMutation()
  const { isReadOnly } = useIsReadOnly()
  const openHours = location?.open_hours?.length > 0 ? location.open_hours : getDefaultHours()

  // Single form context for the entire page
  const form = useAppForm({
    defaultValues: {
      is_pickup: location?.is_pickup ?? false,
      pickup_cutoff: location?.pickup_cutoff ?? DEFAULT_PICKUP_CUTOFF_HOURS,
      open_hours: openHours,
    },
    validators: {
      onSubmit: locationPickupSettingsSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        try {
          await updateLocationSettingsMutation.mutateAsync({
            locationId,
            previousIsPickup: location?.is_pickup ?? false,
            ...value,
          })

          // Reset the form with the updated values after successful save
          const updatedValues = {
            is_pickup: value.is_pickup,
            pickup_cutoff: value.pickup_cutoff,
            open_hours: value.open_hours,
          }
          formApi.reset(updatedValues)

          toast.success('Location settings saved successfully')
        } catch (error) {
          toast.error('Failed to save location settings', {
            description: getErrorMessage(error),
          })
        }
      },
    },
  })

  return (
    <div className="">
      <DeliveryAndAvailabilityHeader
        location={location}
        icon={Clock}
        title="Store Hours & Settings"
        description="Manage operating hours and other settings"
      />

      <form.AppForm aria-label="Store Hours and Settings Form">
        <div className="flex flex-col gap-4">
          <FulfillmentSettingsInputs
            title="In-Store Pickup Settings"
            subtitle="Adjust your In-Store Pickup Settings. This will be used to determine if the location is available for in-store pickup and how many hours before close an order must be placed to be available for pickup."
            fulfillmentSettings={{
              fulfillment_type: FulfillmentType.Pickup.Value,
              is_fulfillment_enabled: location?.is_pickup ?? false,
              cutoff: location?.pickup_cutoff ?? DEFAULT_PICKUP_CUTOFF_HOURS,
            }}
          />

          <WeeklyHoursInputs
            title="Operating Hours"
            subtitle="Set your operating hours for each day of the week"
            initialHours={openHours}
            fieldName="open_hours"
          />
        </div>
        {!isReadOnly ? <SaveBar mutation={updateLocationSettingsMutation} /> : null}
      </form.AppForm>
    </div>
  )
}
